import React, { useState, useEffect } from 'react';
import { Formik, Form } from 'formik';
import { Box } from '@mui/material';
import { useSelector } from 'react-redux';
import { FlexContainer } from '@/components/containers/FlexContainer/FlexContainer';
import { GMapsComponent } from '@/components/reusable/GMapsComponent/GMapsComponent';
import { COWORKING, MEETING_ROOM } from '@/types/spaceTypes.constants';
import './BookingConfirmationView.scss';

import Label from '@/components/form/Label/Label';
import { minWidth } from '@mui/system';

const GoogleIcon = props => {
  const { iconName, fontSize = '14px', className = '', style = {} } = props;
  useEffect(() => {
    const link = document.createElement('link');
    link.href =
      'https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
  }, []);

  return (
    <span
      className={`material-symbols-outlined ${className}`}
      style={{
        fontSize: fontSize,
        width: '16px',
        ...style,
      }}
    >
      {iconName}
    </span>
  );
};

const parseMainImage = mainImageStr => {
  try {
    const images = JSON.parse(mainImageStr);
    if (Array.isArray(images) && images.length > 0) {
      return encodeURI(images[0].trim());
    }
  } catch (error) {
    console.error('❌ Error al parsear main_image:', error);
  }

  return 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c';
};

const BookingConfirmation = props => {
  const { initialValues, languageData, reservation } = props;
  const currentSearchFormData = useSelector(state => state.searchFormData);
  const currencyData = useSelector(state => state.currency);
  const currencyFromRedux = currencyData?.currency;
  const guests = currentSearchFormData?.guests;
  const startTimeFromRedux = currentSearchFormData?.startTime || '8:00 AM';
  const endTimeFromRedux = currentSearchFormData?.endTime || '7:00 PM';

  const [backgroundImage, setBackgroundImage] = useState(() => {
    if (reservation?.main_image) {
      return parseMainImage(reservation.main_image);
    }
    return 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c';
  });

  useEffect(() => {
    if (reservation?.main_image) {
      const parsed = parseMainImage(reservation.main_image);
      setBackgroundImage(parsed);
    }
  }, [reservation]);

  return (
    <FlexContainer
      className={`flex-column padlr10 justify-start items-center fontWhite booking-confirmation-container ${
        backgroundImage ? 'bgDynamic' : 'bgGradientBlue'
      }`}
      style={
        backgroundImage ? { backgroundImage: `url(${backgroundImage})` } : {}
      }
    >
      <FlexContainer
        className={'flex-column padlr10 margb30 booking-confirmation-content'}
      >
        <Label className={'text-24 margt60'}>
          {languageData === 'EN'
            ? 'Booking Confirmation'
            : 'Confirmación de Reserva'}
        </Label>

        {reservation?.subtype_title === 'Coworking' && (
          <Label className={'text-14 margtb10 opacity-70 whitespace-preline'}>
            {languageData === 'EN' ? 'Single Day Pass' : '1 Pase Individual'}
          </Label>
        )}

        {reservation?.subtype_title !== 'Coworking' && (
          <Label className={'text-14 margtb10 opacity-70 whitespace-preline'}>
            {guests.length === 1 && (
              <>
                {languageData === 'EN'
                  ? 'One Private Meeting Room Reservation'
                  : '1 Reservación de Sala de Juntas Privada'}
              </>
            )}
            {guests.length >= 1 && (
              <>
                {guests.length}
                {languageData === 'EN'
                  ? ' Private Meeting Room Reservations'
                  : ' Reservaciones de Salas de Juntas Privadas'}
              </>
            )}
          </Label>
        )}

        <FlexContainer
          className={
            'flex-column margtb10 borderrad32 overflow-hidden  position-relative'
          }
          style={{
            maxWidth: '800px',
            backgroundColor: 'rgba(10,10,10,0.08)',
          }}
        >
          <Box
            className={
              'width100 height-220px overflow-hidden position-relative index-1'
            }
          >
            <img
              src={backgroundImage}
              alt={reservation?.subtype_title || 'Space'}
              className={'width100 height100 objectCover'}
            />

            <Box className={'width100 height100 pos-abs top0 left0 index-2'} />
          </Box>

          <Box
            className={'pos-rel padtb12 padlr16 index-3'}
            style={{
              backgroundColor: 'rgba(10,10,10,0.07)',
              borderRadius: '0 0 32px 32px',
              backdropFilter: 'blur(24px)',
            }}
          >
            <Formik initialValues={initialValues}>
              {({}) => (
                <Form className={'width100'}>
                  <Box>
                    <Label className={'text-18 margb10'}>
                      {reservation?.space_name
                        ? reservation.space_name
                        : languageData === 'EN'
                          ? 'Popnest Coworking Studio'
                          : 'Estudio Popnest Coworking'}
                    </Label>
                  </Box>
                  <Box mb={1} position="relative">
                    <Box className="pos-rel width100">
                      <Box
                        className="grayShadowBottom placeholder-custom width-302px padt30 padr14 padb10 padl12 fontWhite text-14 boxSizing_borderBox borderrad12"
                        style={{
                          backgroundColor: 'rgba(10,10,10,0.15)',
                          border: '0px solid rgba(10,10,10,0.4)',
                          transition: 'all 0.2s ease',
                        }}
                      >
                        {reservation?.space_address
                          ? reservation.space_address
                          : languageData === 'EN'
                            ? 'Address not available'
                            : 'Dirección no disponible'}
                      </Box>
                      <Box className="pos-abs top8 left8 gap-4 flex-container">
                        <GoogleIcon
                          className={'fontWhite'}
                          iconName="location_on"
                          fontSize="16px"
                        />

                        <label className="text-14 pointerEventsNone text-weight-500">
                          {languageData === 'EN' ? 'Location' : 'Ubicación'}
                        </label>
                      </Box>
                    </Box>
                  </Box>

                  <FlexContainer className=" gap-10">
                    <Box mb={1} position="relative" style={{ flex: 1 }}>
                      <Box className="pos-rel width100">
                        <Box
                          className="grayShadowBottom placeholder-custom width100 padt30 padr28 padb10 padl8 fontWhite text-15 boxSizing_borderBox borderrad12 align-items-center flex-container gap-8"
                          style={{
                            backgroundColor: 'rgba(10,10,10,0.15)',
                            border: '0px solid rgba(10,10,10,0.4)',
                            transition: 'all 0.2s ease',
                            minHeight: '52px',
                          }}
                        >
                          <GoogleIcon
                            iconName="calendar_month"
                            fontSize="16px"
                          />
                          {console.log('space detail:', reservation)}

                          <Box>
                            {reservation?.start_date &&
                            !isNaN(new Date(reservation.start_date))
                              ? new Date(
                                  reservation?.start_date.split('-').join('/'),
                                ).toLocaleDateString('es-ES', {
                                  day: '2-digit',
                                  month: 'short',
                                  year: 'numeric',
                                  timeZone: 'UTC',
                                })
                              : 'Sin fecha'}
                          </Box>
                        </Box>

                        <label className="pos-abs top8 left8 text-14 pointerEventsNone text-weight-500">
                          {languageData === 'EN'
                            ? 'Check-in Date'
                            : 'Fecha de Check-in'}
                        </label>
                      </Box>
                    </Box>

                    <Box mb={1} position="relative" style={{ flex: 1 }}>
                      <Box className="pos-rel width100">
                        <Box
                          className="grayShadowBottom placeholder-custom width100  padr8 padl10 fontWhite text-15 boxSizing_borderBox borderrad12 align-items-center flex-container justify-between"
                          style={{
                            backgroundColor: 'rgba(10,10,10,0.15)',
                            border: '0px solid rgba(10,10,10,0.4)',
                            transition: 'all 0.2s ease',
                            minHeight: '52px',
                          }}
                        >
                          <GoogleIcon
                            className="padt16"
                            iconName="map"
                            fontSize="20px"
                          />

                          <Box className=" placeholder-custom-map padl28 padt6 padb6 width-70px height-50px overflow-hidden borderrad10">
                            <GMapsComponent
                              spaceData={{
                                space_latitude: '19.3522',
                                space_logitude: '-99.1616',
                              }}
                              customHeight={'50px'}
                              setIsFullScreenMap={() => {}}
                            />
                          </Box>
                        </Box>
                        <Box className="pos-abs top8 left10 flex-container align-items-center gap-8px">
                          <label className="text-14 pointerEventsNone text-weight-500">
                            {languageData === 'EN' ? 'Map' : 'Mapa'}
                          </label>
                        </Box>
                      </Box>
                    </Box>
                  </FlexContainer>

                  <FlexContainer className=" gap-10">
                    <Box mb={1} position="relative" style={{ flex: 1 }}>
                      <Box className="pos-rel width100">
                        <Box
                          className="grayShadowBottom placeholder-custom width100 padt30 padr64 padb10 padl8 fontWhite text-12 boxSizing_borderBox borderrad12 flex-container align-items-center gap-8"
                          style={{
                            backgroundColor: 'rgba(10,10,10,0.15)',
                            border: '0px solid rgba(10,10,10,0.4)',
                            transition: 'all 0.2s ease',
                            minHeight: '52px',
                          }}
                        >
                          <GoogleIcon iconName="alarm" fontSize="20px" />
                          <Box>After {startTimeFromRedux}</Box>
                        </Box>

                        <label className="pos-abs top8 left8 text-14 pointerEventsNone text-weight-500">
                          {languageData === 'EN'
                            ? 'Check-in Time'
                            : 'Hora de Check-in'}
                        </label>
                      </Box>
                    </Box>
                    <Box mb={1} position="relative" style={{ flex: 1 }}>
                      <Box className="pos-rel width100">
                        <Box
                          className="grayShadowBottom placeholder-custom width100 padt30 padr64 padb10 padl8 fontWhite text-12 boxSizing_borderBox borderrad12 flex-container align-items-center gap-8"
                          style={{
                            backgroundColor: 'rgba(10,10,10,0.15)',
                            border: '0px solid rgba(10,10,10,0.4)',
                            transition: 'all 0.2s ease',
                            minHeight: '52px',
                          }}
                        >
                          <GoogleIcon iconName="alarm" fontSize="20px" />
                          <Box>Before {endTimeFromRedux}</Box>
                        </Box>

                        <label className="pos-abs top8 left8 text-14 pointerEventsNone text-weight-500">
                          {languageData === 'EN'
                            ? 'Check-out time'
                            : 'Hora de Check-out'}
                        </label>
                      </Box>
                    </Box>
                  </FlexContainer>

                  <Box mb={1} position="relative">
                    <Box className="pos-rel width100">
                      <Box
                        className={`grayShadowBottom placeholder-custom ${
                          languageData === 'EN' ? 'width-140px' : 'width-172px'
                        } padt30 padr14 padb10 padl8 fontWhite text-14 boxSizing_borderBox borderrad12 flex-container align-items-center gap-8`}
                        style={{
                          backgroundColor: 'rgba(10,10,10,0.15)',
                          border: '0px solid rgba(10,10,10,0.4)',
                          transition: 'all 0.2s ease',
                          minHeight: '52px',
                        }}
                      >
                        <i
                          className="fa-solid fa-user "
                          style={{
                            fontSize: '14px',
                            width: '16px',
                          }}
                        ></i>

                        <Box>{guests.length || 0}</Box>
                      </Box>

                      <label className="pos-abs top8 left10 text-14 pointerEventsNone text-weight-500">
                        {languageData === 'EN'
                          ? 'Number of guests'
                          : 'Número de asistentes'}
                      </label>
                    </Box>
                  </Box>

                  <Box mb={1} position="relative">
                    {guests.map(guest => (
                      <Box mb={1} className="pos-rel width100">
                        <Box
                          className="grayShadowBottom placeholder-custom width100 padt10  padb10 padl12 fontWhite text-14 boxSizing_borderBox borderrad12"
                          style={{
                            backgroundColor: 'rgba(10,10,10,0.15)',
                            border: '0px solid rgba(10,10,10,0.4)',
                            transition: 'all 0.2s ease',
                          }}
                        >
                          <Box
                            mb={2}
                            className="text-14 fontWhite text-weight-500"
                          >
                            {languageData === 'EN' ? 'Guest' : 'Invitado'}
                          </Box>

                          <FlexContainer className=" margb4">
                            <Box style={{ flex: 1 }}>
                              <label className="text-14 fontWhite block margb4 text-weight-500">
                                {languageData === 'EN' ? 'Name' : 'Nombre'}
                              </label>
                              <Box className=" placeholder-custom width100  padr10 padb10  fontWhite text-14 boxSizing_borderBox borderrad8 text-weight-500">
                                {guest.firstName}
                              </Box>
                            </Box>
                            <Box style={{ flex: 1 }}>
                              <label className="text-14 fontWhite  block margb4">
                                {languageData === 'EN'
                                  ? 'Last Name'
                                  : 'Apellido'}
                              </label>
                              <Box className="placeholder-custom width100   padb10  fontWhite text-14 boxSizing_borderBox borderrad8 text-weight-500">
                                {guest.lastName}
                              </Box>
                            </Box>
                          </FlexContainer>

                          <Box>
                            <label className="text-14 fontWhite  block margb4">
                              {languageData === 'EN'
                                ? 'Email'
                                : 'Correo electrónico'}
                            </label>
                            <Box className=" width100  padr10 padb10  fontWhite text-14 boxSizing_borderBox borderrad8 text-weight-500">
                              {guest.email}
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    ))}
                  </Box>

                  <Box mb={1} position="relative">
                    <Box className="pos-rel width100">
                      <Box
                        className="grayShadowBottom placeholder-custom width100 padt10 padr14 padb10 padl12 fontWhite text-14 boxSizing_borderBox borderrad12"
                        style={{
                          backgroundColor: 'rgba(10,10,10,0.15)',
                          border: '0px solid rgba(10,10,10,0.4)',
                          transition: 'all 0.2s ease',
                        }}
                      >
                        <Box
                          mb={2}
                          className="text-18 fontWhite text-weight-500"
                        >
                          {languageData === 'EN'
                            ? 'Space Rules'
                            : 'Reglas del Espacio'}
                        </Box>

                        <Box
                          component="ul"
                          className="text-18 fontWhite padl24 marg0"
                        >
                          <li className="margb8 fontWhite text-12">
                            {languageData === 'EN'
                              ? 'Use the space only for the booked purpose (e.g., meetings, work sessions, shoots).'
                              : 'Usa el espacio solo para el propósito reservado (ej. reuniones, sesiones de trabajo, sesiones de fotos).'}
                          </li>
                          <li className="margb8 fontWhite text-12">
                            {languageData === 'EN'
                              ? 'Keep noise at a reasonable level. Avoid disrupting other people or neighbors.'
                              : 'Mantén el ruido en un nivel razonable. Evita molestar a otras personas o vecinos.'}
                          </li>
                          <li className="margb8 fontWhite text-12">
                            {languageData === 'EN'
                              ? 'Do not share entry codes or access details with unregistered people.'
                              : 'No compartas códigos de entrada o detalles de acceso con personas no registradas.'}
                          </li>
                        </Box>
                      </Box>
                    </Box>
                  </Box>

                  <Box mb={1} position="relative">
                    <Box className="pos-rel width100">
                      <Box
                        className="grayShadowBottom placeholder-custom width100 padt10 padr14 padb20 padl14 fontWhite text-14 boxSizing_borderBox borderrad12"
                        style={{
                          backgroundColor: 'rgba(10,10,10,0.15)',
                          border: '0px solid rgba(10,10,10,0.4)',
                          transition: 'all 0.2s ease',
                        }}
                      >
                        <Box className="text-18 fontWhite margb8 text-weight-500">
                          {languageData === 'EN'
                            ? 'Cancellation Policy'
                            : 'Política de Cancelación'}
                        </Box>

                        <Box
                          className="text-12 fontWhite lineHeight18"
                          style={{ opacity: 0.9 }}
                        >
                          {languageData === 'EN'
                            ? "At the moment, we don't offer refunds for cancellations. However, you can reschedule your reservation for another day and time, based on the space's availability."
                            : 'Por el momento, no ofrecemos reembolsos por cancelaciones. Sin embargo, puedes reprogramar tu reserva para otro día y hora, según la disponibilidad del espacio.'}
                        </Box>
                      </Box>
                    </Box>
                  </Box>

                  <Box mb={1} position="relative">
                    <Box className="pos-rel width100">
                      <Box
                        className="grayShadowBottom placeholder-custom width100 padt10 padr14 padb20 padl14 fontWhite text-14 boxSizing_borderBox borderrad12"
                        style={{
                          backgroundColor: 'rgba(10,10,10,0.15)',
                          border: '0px solid rgba(10,10,10,0.4)',
                          transition: 'all 0.2s ease',
                        }}
                      >
                        <Box className="text-18 fontWhite margb10 text-weight-500">
                          {languageData === 'EN' ? 'Payment' : 'Pago'}
                        </Box>

                        <Box
                          className=" width100  padr14   fontWhite text-14 boxSizing_borderBox borderrad12 margb12 align-items-center"
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                          }}
                        >
                          <Box>Total ({currencyFromRedux})</Box>
                          <Box className="text-16">
                            {' '}
                            {reservation?.final_price
                              ? `$${reservation?.final_price} ${currencyFromRedux}`
                              : `$0 ${currencyFromRedux}`}
                          </Box>
                        </Box>

                        <Box
                          className="text-12 fontWhite lineHeight18 margb16"
                          style={{
                            opacity: 0.9,
                          }}
                        >
                          {languageData === 'EN'
                            ? `Your total is $${Number(
                                reservation?.final_price || 0,
                              ).toLocaleString(
                                'es-MX',
                              )} ${currencyFromRedux}, with service fees and taxes already included.`
                            : `Tu total es $${Number(
                                reservation?.final_price || 0,
                              ).toLocaleString(
                                'es-MX',
                              )} ${currencyFromRedux}, con tarifas de servicio e impuestos ya incluidos.`}
                        </Box>

                        <FlexContainer className="gap-20 margb10 justify-start">
                          <Box className="borderrad12  fontWhite text-14 cursor-pointer margr8">
                            {languageData === 'EN'
                              ? 'Get receipt'
                              : 'Obtener recibo'}
                          </Box>
                          <Box className="borderrad12  fontWhite text-14 cursor-pointer">
                            {languageData === 'EN'
                              ? 'Get invoice'
                              : 'Obtener factura'}
                          </Box>
                        </FlexContainer>
                      </Box>
                    </Box>
                  </Box>

                  <Box mb={1} position="relative">
                    <Box className="pos-rel width100">
                      <Box
                        className="grayShadowBottom placeholder-custom width100 padt10 padr14 padb20 padl14 fontWhite text-14 boxSizing_borderBox borderrad12"
                        style={{
                          backgroundColor: 'rgba(10,10,10,0.15)',
                          border: '0px solid rgba(10,10,10,0.4)',
                          transition: 'all 0.2s ease',
                        }}
                      >
                        <Box className="text-18 fontWhite margb8 text-weight-500">
                          {languageData === 'EN'
                            ? 'Get in touch with Popnest'
                            : 'Ponte en contacto con Popnest'}
                        </Box>

                        <Box
                          className="text-12 fontWhite lineHeight18"
                          style={{ opacity: 0.9 }}
                        >
                          {languageData === 'EN'
                            ? 'Our team is available to help you with any questions about your reservation at info@popnest.org'
                            : 'Nuestro equipo está disponible para ayudarte con cualquier pregunta sobre tu reservación en info@popnest.org'}
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Form>
              )}
            </Formik>
          </Box>
        </FlexContainer>
      </FlexContainer>
    </FlexContainer>
  );
};

export default BookingConfirmation;
